import {
  InputRule,
  Node,
  mergeAttributes,
  nodeInputRule,
  nodePasteRule,
} from '@tiptap/core';
import { officialEmotes } from './official-emotes';
import escapeRegExp from 'lodash/escapeRegExp';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { isValidFileSrc } from '../../components/utils/xss';

import s from './viewer.scss';

// Extract the codes
const officialEmotesCodes = Object.keys(officialEmotes);

// We anchor to the end of the text, this is because tiptap gives us the leaf of where
// we are typing so if you start typing inside a line it will still match the end.
// This should give good balance of converting what the user is typing not what a paste
// or edit created. Also seems all other input rules use end anchoring so I am guessing
// this is by design.
const regexWrapper = emotes => `(${emotes})$`;

// This creates a single huge! regex for all emotes
const officialEmotesRegex = new RegExp(
  regexWrapper(officialEmotesCodes.map(escapeRegExp).join('|'))
);

// Paste regex does the entire pasted text so we need global and no anchoring
const officialEmotesRegexForPaste = new RegExp(
  officialEmotesCodes.map(escapeRegExp).join('|'),
  'g'
);

export interface EmoteOptions {
  HTMLAttributes: Record<string, any>;
}

export interface CommunityEmote {
  src: string;
  width: string;
  height: string;
  url: string;
  title: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emote: {
      setEmote: (id: string) => ReturnType;
      setEmoji: (emoji: string) => ReturnType;
      setCommunityEmote: (emote: CommunityEmote) => ReturnType;
      setAvatar: (emote: CommunityEmote) => ReturnType;
    };
  }
}
// Fix for emote typed on new line causing newline break - https://github.com/ueberdosis/tiptap/issues/4796
// This is merged now just waiting on next release of TipTap
export const Emote = Node.create<EmoteOptions>({
  name: 'da-emote',
  needsFixAfterPaste: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      'data-emote': {
        default: null,
      },
      'data-type': {
        default: 'official',
      },
      src: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      url: {
        default: null,
      },
      title: {
        default: null,
      },
    };
  },

  inline: true,
  group: 'inline',
  draggable: true,

  parseHTML() {
    return [
      { tag: 'img[data-emote]' },
      {
        tag: 'a[data-emote]',
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }

          const dataType = node.getAttribute('data-emote');
          if (!dataType) {
            return false;
          }
          try {
            return {
              'data-emote': node.getAttribute('data-emote'),
              'data-type': node.getAttribute('data-type'),
              src: node.getAttribute('src') ?? node.getAttribute('data-src'),
              width:
                node.getAttribute('width') ?? node.getAttribute('data-width'),
              height:
                node.getAttribute('height') ?? node.getAttribute('data-height'),
              url: node.getAttribute('url') ?? node.getAttribute('data-url'),
              title:
                node.getAttribute('title') ?? node.getAttribute('data-title'),
            };
          } catch (e) {}
          return null;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attribs = mergeAttributes(this.options.HTMLAttributes, {
      ...HTMLAttributes,
      width: HTMLAttributes.width,
      height: HTMLAttributes.height,
      title: HTMLAttributes.title,
      'data-url': HTMLAttributes.url,
      'data-type': HTMLAttributes['data-type'],
      class: s['emote'],
    });

    if (HTMLAttributes['data-type'] === 'official') {
      const emote =
        officialEmotes[HTMLAttributes['data-emote']] ??
        officialEmotes[':deviantart:'];

      return [
        'img',
        mergeAttributes(this.options.HTMLAttributes, {
          ...attribs,
          src: `https://e.deviantart.net/emoticons${emote.src}`,
          width: emote.width,
          height: emote.height,
          alt: emote.name,
        }),
      ];
    }

    if (HTMLAttributes['data-type'] === 'community') {
      return [
        'a',
        mergeAttributes(this.options.HTMLAttributes, {
          ...attribs,
          href: this.editor?.isEditable
            ? // eslint-disable-next-line no-script-url
              'javascript:void(0)'
            : HTMLAttributes.url,
        }),
        [
          'img',
          mergeAttributes(this.options.HTMLAttributes, {
            ...attribs,
            src: isValidFileSrc(HTMLAttributes.src) ? HTMLAttributes.src : '',
            width: HTMLAttributes.width,
            height: HTMLAttributes.height,
            alt: HTMLAttributes.title,
          }),
        ],
      ];
    }

    if (HTMLAttributes['data-type'] === 'user') {
      return [
        'a',
        mergeAttributes(this.options.HTMLAttributes, {
          ...attribs,
          href: this.editor?.isEditable
            ? // eslint-disable-next-line no-script-url
              'javascript:void(0)'
            : HTMLAttributes.url,
        }),
        [
          'img',
          mergeAttributes(this.options.HTMLAttributes, {
            ...attribs,
            src: /^https:\/\/a\.deviantart\.net\/avatars/.test(
              HTMLAttributes.src
            )
              ? HTMLAttributes.src
              : '',
            width: HTMLAttributes.width,
            height: HTMLAttributes.height,
            alt: HTMLAttributes.title,
          }),
        ],
      ];
    }

    return ['span'];
  },

  renderText() {
    return '';
  },

  addCommands() {
    return {
      setEmote:
        emote =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { 'data-emote': emote },
          });
        },
      setEmoji:
        emoji =>
        ({ tr }) => {
          const { $from } = tr.selection;
          const pos = tr.mapping.map($from.pos);
          tr.insertText(emoji, pos);
          return true;
        },
      setCommunityEmote:
        ({ src, title, width, height, url }) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              'data-emote': '-community-',
              'data-type': 'community',
              src,
              title,
              width,
              height,
              url,
            },
          });
        },
      setAvatar:
        ({ src, title, width, height, url }) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              'data-emote': '-user-',
              'data-type': 'user',
              src,
              title,
              width,
              height,
              url,
            },
          });
        },
    };
  },

  /**
   *  Handles the typed emote codes
   */
  addInputRules() {
    const rules: InputRule[] = [];
    rules.push(
      nodeInputRule({
        find: officialEmotesRegex,
        type: this.type,
        getAttributes(match) {
          return {
            'data-emote': match[0].trim(),
          };
        },
      })
    );
    return rules;
  },

  /**
   * Handle pasting of emotes codes, this is important because
   * non converted codes corrupt the editor when typing and the
   * input rule above matches more than one item.
   */
  addPasteRules() {
    return [
      nodePasteRule({
        find: officialEmotesRegexForPaste,
        type: this.type,
        getAttributes: match => {
          (Emote as any).needsFixAfterPaste = true;
          return {
            'data-emote': match[0],
          };
        },
      }),
    ];
  },

  /**
   * Handles backspace where the browser deletes the element before tiptap
   * can see it, this then leaves a text node with matching code which then
   * confuses the input rule. This handles the backspace and delete the emote
   * before the browser can.
   *
   * see T65798
   */
  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          const { empty, anchor } = state.selection;

          if (!empty) {
            return false;
          }

          let isBackspaceHandled = false;

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === 'da-emote') {
              tr.deleteRange(pos, pos + node.nodeSize);
              isBackspaceHandled = true;
              return false;
            }
          });

          return isBackspaceHandled;
        }),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('da-emote-paste'),
        props: {
          /**
           * This fixes a bug in the nodePasteRule aboove where pasting
           * would move the selection to the last pasted emote
           * not the end of the pasted text.
           */
          handlePaste(view, event, slice) {
            requestAnimationFrame(() => {
              if ((Emote as any).needsFixAfterPaste) {
                const tr = view.state.tr;
                // Get all nodes between the pasted range
                view.state.tr.doc.nodesBetween(
                  tr.selection.from,
                  tr.selection.to,
                  (node, pos) => {
                    // Set the selection to the end of the node range
                    const st = TextSelection.create(
                      tr.doc,
                      pos + node.nodeSize
                    );
                    view.dispatch(tr.setSelection(st));
                  }
                );
                view.focus();
                (Emote as any).needsFixAfterPaste = false;
              }
            });
          },
        },
      }),
    ];
  },
});
